import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPinned } from "lucide-react";

const PALETTE = ["#0052FF", "#059669", "#D97706", "#DB2777", "#7C3AED", "#0891B2", "#DC2626", "#65A30D"];
const UNASSIGNED_COLOR = "#94A3B8";

const colorForArea = (area, teamColorMap) => {
  const firstTeam = area.teams?.[0];
  return firstTeam ? teamColorMap.get(firstTeam.team_id) || UNASSIGNED_COLOR : UNASSIGNED_COLOR;
};

export const ServiceAreasCoverageMap = ({ areas, availableTeams, availableServices }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerGroupRef = useRef(null);
  const shouldFitRef = useRef(true);
  const [teamFilter, setTeamFilter] = useState("all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const teamColorMap = new Map((availableTeams || []).map((t, i) => [t.team_id, PALETTE[i % PALETTE.length]]));

  const activeAreas = (areas || []).filter((a) => a.status === "Active" && a.geography_preview);
  const visibleAreas = activeAreas.filter((a) => {
    if (teamFilter !== "all" && !a.teams.some((t) => t.team_id === teamFilter)) return false;
    if (areaFilter !== "all" && a.service_area_id !== areaFilter) return false;
    if (serviceFilter !== "all" && a.service_ids?.length > 0 && !a.service_ids.includes(serviceFilter)) return false;
    return true;
  });

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { attributionControl: false, zoomAnimation: false, fadeAnimation: false, markerZoomAnimation: false }).setView([39.5, -98.35], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(map);
    mapRef.current = map;
    layerGroupRef.current = L.layerGroup().addTo(map);
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) { try { map.invalidateSize(); } catch { /* ignore */ } } }, 0);
    return () => { cancelled = true; clearTimeout(t); try { map.remove(); } catch { /* ignore */ } mapRef.current = null; layerGroupRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const group = layerGroupRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const bounds = L.latLngBounds([]);

    visibleAreas.forEach((area) => {
      const color = colorForArea(area, teamColorMap);
      const preview = area.geography_preview;
      let layer;
      if (preview.kind === "radius" && preview.center) {
        const [lng, lat] = preview.center;
        layer = L.circle([lat, lng], { radius: (preview.radius_miles || 10) * 1609.34, color, weight: 2, fillOpacity: 0.25 });
      } else if (preview.geometry) {
        layer = L.geoJSON(preview.geometry, { style: { color, weight: 2, fillOpacity: 0.25 } });
      }
      if (!layer) return;
      layer.on("click", () => setSelected(area));
      layer.addTo(group);
      try { bounds.extend(layer.getBounds()); } catch { /* ignore */ }
    });

    // Only auto-fit the viewport on the initial load or when the user changes a filter -
    // never on a routine background data refresh, so manual pan/zoom is preserved.
    if (!shouldFitRef.current || !bounds.isValid()) return;
    shouldFitRef.current = false;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled || !mapRef.current) return;
      try { mapRef.current.invalidateSize(); mapRef.current.fitBounds(bounds, { padding: [24, 24], animate: false }); } catch { /* leaflet race on unmount */ }
    }, 0);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFilter, areaFilter, serviceFilter, areas]);

  useEffect(() => {
    // A deliberate filter change should re-frame the map; a background poll refresh should not.
    shouldFitRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFilter, areaFilter, serviceFilter]);

  return (
    <div data-testid="service-areas-coverage-map" className="flex h-full flex-col">
      <div className="mb-3 flex flex-wrap items-center gap-2 shrink-0">
        <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} data-testid="coverage-map-team-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
          <option value="all">All Teams</option>
          {(availableTeams || []).map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
        </select>
        <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} data-testid="coverage-map-area-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
          <option value="all">All Service Areas</option>
          {activeAreas.map((a) => <option key={a.service_area_id} value={a.service_area_id}>{a.name}</option>)}
        </select>
        <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} data-testid="coverage-map-service-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
          <option value="all">All Services</option>
          {(availableServices || []).map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
        </select>
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        <div ref={elRef} data-testid="coverage-map-canvas" className="h-full flex-1 rounded-2xl border border-slate-200 dark:border-white/10" />
        <div className="w-64 shrink-0 overflow-y-auto rounded-2xl border border-slate-200 p-4 dark:border-white/10" data-testid="coverage-map-detail">
          {!selected ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-xs text-slate-400">
              <MapPinned size={20} className="text-slate-300 dark:text-slate-600" />
              Click an area on the map to see its details.
            </div>
          ) : (
            <div data-testid="coverage-map-detail-content">
              <p className="text-sm font-bold text-slate-950 dark:text-white">{selected.name}</p>
              <p className="mt-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">{selected.status}</p>
              <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Boundary Type</p>
              <p className="text-xs text-slate-600 dark:text-slate-300">{selected.geography_summary?.kind === "radius" ? "Radius" : "Boundary/Polygon"}</p>
              <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Team(s)</p>
              <p className="text-xs text-slate-600 dark:text-slate-300">{selected.teams.length ? selected.teams.map((t) => t.name).join(", ") : "Unassigned"}</p>
              <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Services</p>
              <p className="text-xs text-slate-600 dark:text-slate-300">{selected.services.length ? selected.services.map((s) => s.name).join(", ") : "All Services"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
